import { useCallback } from 'react';

import { usePaymentContext } from 'features/payment';
import { isCashApp } from 'features/payment/utils';
import { useStoreContext } from 'features/store';

import { useCartHasUnavailableOffers } from 'pages/cart/hooks/use-cart-has-unavailable-offers';
import { useLoyaltyIncentivesAvailability } from 'pages/loyalty/loyalty-incentives-components/use-loyalty-incentives-availability';
import { selectors, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

import { CheckoutError } from '../types';

import { useCheckoutContext } from './use-checkout-context';

export const TipAmounts = {
  PERCENT_AMOUNTS: [15, 18, 20],
  DOLLAR_AMOUNTS: [2, 3, 4],
  PERCENT_DEFAULT: 18,
  DOLLAR_DEFAULT: 3,
  STYLE_THRESHOLD: 1500,
  MAX_AMOUNT: 5000,
};

const useOffersValidation = () => {
  const selectedLoyaltyOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);
  const { checkLoyaltyOfferAvailability } = useLoyaltyIncentivesAvailability();
  const cartHasUnavailableOffers = useCartHasUnavailableOffers();

  return useCallback((): CheckoutError[] => {
    const isSelectedLoyaltyOfferAvailable =
      !selectedLoyaltyOffer || checkLoyaltyOfferAvailability(selectedLoyaltyOffer);

    // TODO: this validates 2 different things but only uses one error message
    const isSelectedOfferOrCartOffersUnavailable =
      !isSelectedLoyaltyOfferAvailable || cartHasUnavailableOffers;
    const offerIsValid = !isSelectedOfferOrCartOffersUnavailable;
    if (offerIsValid) {
      return [];
    }

    return [
      {
        placement: 'errorDialog',
        // TODO: this message is not good, consider using a more descriptive message
        message: t('invalidOfferError'),
      },
    ];
  }, [cartHasUnavailableOffers, checkLoyaltyOfferAvailability, selectedLoyaltyOffer]);
};

const useVehicleDescriptionValidation = () => {
  const { isVehicleDescriptionEnabled, vehicleDescription } = useCheckoutContext();
  const vehicleDescriptionMaxLength = useFlag(
    LaunchDarklyFlag.CURBSIDE_VEHICLE_DESCRIPTION_MAX_LENGTH
  );

  return useCallback((): CheckoutError[] => {
    if (!isVehicleDescriptionEnabled) {
      return []; // No validation needed if vehicle description is not enabled
    }

    if (!vehicleDescription.value) {
      const errorMessage = t(
        'vehicleDescriptionRequired',
        'You must enter your vehicle description to proceed.'
      );

      return [
        {
          placement: 'errorDialog',
          message: errorMessage,
        },
      ];
    }

    const isValid = vehicleDescriptionMaxLength
      ? vehicleDescription.value?.length > 1 &&
        vehicleDescription.value.length <= vehicleDescriptionMaxLength
      : true;

    if (!isValid) {
      const dialogErrorMessage = t(
        'vehicleDescriptionErrors',
        'You must correct provided vehicle description to proceed.'
      );
      const componentErrorMessage = t('descriptionLengthError', {
        min: 1,
        max: vehicleDescriptionMaxLength,
      });
      return [
        {
          placement: 'errorDialog',
          message: dialogErrorMessage,
        },
        {
          placement: 'vehicleDescription',
          message: componentErrorMessage,
        },
      ];
    }

    return [];
  }, [isVehicleDescriptionEnabled, vehicleDescription, vehicleDescriptionMaxLength]);
};

const useTipValidation = () => {
  const { tipSelection, tipAmount, totals } = useCheckoutContext();
  const { subTotalCents = 0 } = totals || {};
  const { otherAmount } = tipSelection;

  return useCallback((): CheckoutError[] => {
    if (!tipSelection.isOtherSelected) {
      return [];
    }

    // If the user has selected "Other" but hasn't provided an amount, we need to show an error
    // if the user doesn't want to tip they should select 0 explicitly
    if (typeof otherAmount === 'undefined') {
      return [
        {
          placement: 'errorDialog',
          message: t('tipRequired', 'You must select a tip to proceed.'),
        },
      ];
    }

    const isTipLessThanSubTotal = tipAmount <= subTotalCents;
    const isTipLessThanMax = tipAmount <= TipAmounts.MAX_AMOUNT;
    const isValidTip = isTipLessThanSubTotal && isTipLessThanMax;

    if (!isValidTip) {
      return [
        {
          placement: 'tipAmount',
          message: t('tipAmountMaxError', "Can't exceed subtotal or be more than $50"),
        },
        {
          placement: 'errorDialog',
          message: t('tipAmountErrors', 'You must correct provided tip amount to proceed.'),
        },
      ];
    }
    return [];
  }, [tipSelection.isOtherSelected, otherAmount, tipAmount, subTotalCents]);
};

const useCashAppValidation = () => {
  const { totals, checkoutPaymentMethodId } = useCheckoutContext();
  const { grandTotal = 0 } = totals || {};

  return useCallback((): CheckoutError[] => {
    if (!isCashApp(checkoutPaymentMethodId)) {
      return [];
    }

    if (grandTotal < 100) {
      return [
        {
          placement: 'footer',
          message: t(
            'sorryMinimumCashApp',
            'Sorry! Minimum order for paying with Cash App is $1.00. Add more items or change your payment method'
          ),
        },
      ];
    }

    return [];
  }, [checkoutPaymentMethodId, grandTotal]);
};

const useIsGrandTotalTooLowValidation = () => {
  const checkoutDeliveryPriceMinimum = useFlag(LaunchDarklyFlag.OVERRIDE_CHECKOUT_DELIVERY_MINIMUM);
  const { isDeliveryOrder, totals } = useCheckoutContext();

  return useCallback((): CheckoutError[] => {
    const { subTotalCents = 0 } = totals || {};
    const checkoutPriceMinimum = isDeliveryOrder ? checkoutDeliveryPriceMinimum : 0;

    if (subTotalCents >= checkoutPriceMinimum) {
      return [];
    }

    return [
      {
        placement: 'errorDialog',
        message: t('minimumPriceError', 'Your cart does not meet the minimum price required.'),
      },
    ];
  }, [checkoutDeliveryPriceMinimum, isDeliveryOrder, totals]);
};

const usePaymentMethodAvailableValidation = () => {
  const { noPaymentMethodsAvailable } = usePaymentContext();

  return useCallback((): CheckoutError[] => {
    if (!noPaymentMethodsAvailable) {
      return [];
    }

    return [
      {
        placement: 'footer',
        message: t('mustAddPaymentMethod', 'Please add a payment method to continue.'),
      },
    ];
  }, [noPaymentMethodsAvailable]);
};

const useIsStoreAvailableValidation = () => {
  const { isStoreOpenAndAvailable } = useStoreContext();
  return useCallback((): CheckoutError[] => {
    if (!isStoreOpenAndAvailable) {
      return [
        {
          placement: 'footer',
          message: t('storeCurrentlyClosed', 'Restaurant Currently Closed.'),
        },
      ];
    }

    return [];
  }, [isStoreOpenAndAvailable]);
};

export const useCheckoutValidation = () => {
  const validateOffers = useOffersValidation();
  const validateVehicleDescription = useVehicleDescriptionValidation();
  const validateTip = useTipValidation();
  const validateCashApp = useCashAppValidation();
  const validateGrandTotalTooLow = useIsGrandTotalTooLowValidation();
  const validateStoreAvailable = useIsStoreAvailableValidation();
  const validatePaymentMethodAvailable = usePaymentMethodAvailableValidation();

  const validate = useCallback((): CheckoutError[] => {
    const paymentMethodErrors = validatePaymentMethodAvailable();
    const offersError = validateOffers();
    const vehicleDescriptionErrors = validateVehicleDescription();
    const tipErrors = validateTip();
    const cashAppErrors = validateCashApp();
    const grandTotalTooLowErrors = validateGrandTotalTooLow();
    const storeAvailableErrors = validateStoreAvailable();
    return [
      ...paymentMethodErrors,
      ...grandTotalTooLowErrors,
      ...offersError,
      ...vehicleDescriptionErrors,
      ...tipErrors,
      ...cashAppErrors,
      ...storeAvailableErrors,
    ];
  }, [
    validatePaymentMethodAvailable,
    validateOffers,
    validateVehicleDescription,
    validateTip,
    validateCashApp,
    validateGrandTotalTooLow,
    validateStoreAvailable,
  ]);

  return { validate };
};
